import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  ArrowRight, 
  Check, 
  Phone,
  Sparkles,
  Leaf,
  Shield,
  Heart,
  Droplets,
  Zap
} from 'lucide-react';
import { businessInfo, images, pureRenewalProtocol } from '../data/mock';
import { 
  CherryBlossom, 
  BlossomBranch,
  Sparkle 
} from '../components/ui/FloralDecorations';

const PureRenewalPage = () => {
  const detoxAreas = [
    { icon: Shield, title: "Parasite Cleanse", description: "Gentle protocols to support your body's natural defense against parasitic concerns" },
    { icon: Zap, title: "Heavy Metal Detox", description: "Targeted support for addressing heavy metal accumulation safely" },
    { icon: Leaf, title: "Environmental Chemicals", description: "Protocols to support detoxification of environmental toxins" },
    { icon: Heart, title: "Liver & Gallbladder", description: "Support natural detoxification and balanced energy" },
    { icon: Droplets, title: "Kidney & Bladder", description: "Promote clearer hydration and balanced mineral function" },
    { icon: Sparkles, title: "Colon & Lymph", description: "Clear toxins and boost immune function naturally" }
  ];

  return (
    <div className="overflow-hidden">
      <Helmet>
        <title>Pure Renewal Protocol - Guided Detox Program | Serenity Natural Health & Wellness</title>
        <meta name="description" content="Experience the Pure Renewal Protocol - a powerful, all-natural guided detox program available remotely. Support parasite cleanse, heavy metal detox, liver support & more. Call (402) 322-1653." />
        <meta name="keywords" content="pure renewal protocol, detox program, parasite cleanse, heavy metal detox, liver cleanse, kidney support, natural detox, Gordon NE, remote detox program" />
        <link rel="canonical" href="https://serenitynaturalhealthwellness.com/pure-renewal-protocol" />
        <meta property="og:title" content="Pure Renewal Protocol - Guided Detox Program | Serenity Natural Health" />
        <meta property="og:description" content="A powerful, all-natural detox system to reset your body. Guided program available remotely with products shipped to you." />
        <meta property="og:image" content={images.pureRenewalLogo} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Pure Renewal Protocol",
            "description": "A powerful, all-natural detox system designed to reset your body from the inside out. Guided program available remotely.",
            "brand": {
              "@type": "Brand",
              "name": "Serenity Natural Health & Wellness"
            },
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "LocalBusiness",
                "name": "Serenity Natural Health & Wellness",
                "telephone": "(402) 322-1653"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section with Protocol Image */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden" style={{
        background: 'linear-gradient(135deg, #E8F4F8 0%, #F0F7F4 25%, #FFF9F7 50%, #E8F4F8 100%)'
      }}>
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 animate-float-slow opacity-30">
          <CherryBlossom size={80} color="#0D7377" />
        </div>
        <div className="absolute bottom-40 right-20 animate-float opacity-25" style={{ animationDelay: '1s' }}>
          <CherryBlossom size={60} color="#14A3A8" />
        </div>
        <div className="absolute top-1/3 right-1/4 animate-pulse-soft">
          <Sparkle size={24} color="#0D7377" className="opacity-40" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-6 border border-[#0D7377]/30 shadow-sm">
                <Sparkles size={16} className="text-[#0D7377]" />
                <span className="text-sm font-medium text-[#2C2F36]">
                  Guided Detox Program - Available Remotely
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6 leading-tight">
                {pureRenewalProtocol.tagline}
                <span className="block mt-2 text-[#0D7377]">
                  The Pure Renewal Protocol
                </span>
              </h1>
              
              <p className="text-lg text-[#4A5568] mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {pureRenewalProtocol.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a 
                  href={`tel:${businessInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 inline-flex items-center justify-center gap-2 shadow-lg text-white"
                  style={{ background: 'linear-gradient(135deg, #0D7377, #14A3A8)' }}
                >
                  <Phone size={18} />
                  Call or Text: {businessInfo.phone}
                </a>
                <Link 
                  to="/contact" 
                  className="px-8 py-4 bg-white border-2 border-[#0D7377] text-[#0D7377] rounded-full font-semibold hover:bg-[#0D7377]/5 transition-colors inline-flex items-center justify-center gap-2"
                >
                  Request Information
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            
            {/* Protocol Image */}
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src={images.pureRenewalLogo}
                  alt="The Pure Renewal Protocol by Serenity Natural Health & Wellness - Detox Program"
                  className="rounded-3xl shadow-2xl w-full max-w-lg mx-auto"
                  style={{ boxShadow: '0 25px 50px -12px rgba(13, 115, 119, 0.3)' }}
                />
              </div>
              {/* Decorative frame */}
              <div className="absolute -top-4 -right-4 w-full h-full max-w-lg mx-auto border-2 border-[#0D7377]/30 rounded-3xl hidden lg:block" />
            </div>
          </div>
        </div>
      </section>

      {/* What is Pure Renewal Protocol */}
      <section className="section-padding bg-white relative">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(13, 115, 119, 0.1)', color: '#0D7377' }}>
              About The Program
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
              What is The Pure Renewal Protocol?
            </h2>
            <p className="text-lg text-[#4A5568] leading-relaxed">
              The Pure Renewal Protocol is a comprehensive series of detox programs that can be completed 
              throughout the year. It's designed to be accessible to anyone who wants to experience a 
              guided detox program <strong>without needing to come into the office</strong> or schedule 
              a consultation first.
            </p>
          </div>

          {/* How it Works */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {pureRenewalProtocol.howItWorks.map((step, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-gradient-to-b from-[#E8F4F8] to-white border border-[#0D7377]/10">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold"
                  style={{ background: 'linear-gradient(135deg, #0D7377, #14A3A8)' }}
                >
                  {index + 1}
                </div>
                <p className="text-[#4A5568] font-medium">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detox Areas */}
      <section className="section-padding" style={{ background: 'linear-gradient(180deg, #F0F7F4 0%, #E8F4F8 50%, white 100%)' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(13, 115, 119, 0.1)', color: '#0D7377' }}>
              Comprehensive Support
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              What The Protocol Includes
            </h2>
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              The Pure Renewal Protocol provides detox and cleansing support for multiple body systems
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {detoxAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-2xl shadow-sm border border-[#0D7377]/10 hover:shadow-md transition-shadow"
                >
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(13, 115, 119, 0.1)' }}>
                    <IconComponent size={28} className="text-[#0D7377]" />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-2">
                    {area.title}
                  </h3>
                  <p className="text-[#4A5568]">{area.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section with Julie's Photo */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(13, 115, 119, 0.1)', color: '#0D7377' }}>
                Your Guide
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                Guided Support Every Step of the Way
              </h2>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                When you join the Pure Renewal Protocol, you're not alone. You'll be added to a 
                private support group where you'll receive guidance and encouragement throughout 
                your detox journey from {businessInfo.owner.name}, {businessInfo.owner.credentials}.
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-full mt-1" style={{ background: 'rgba(13, 115, 119, 0.1)' }}>
                    <Check size={16} className="text-[#0D7377]" />
                  </div>
                  <span className="text-[#4A5568]">Products shipped directly to your door</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-full mt-1" style={{ background: 'rgba(13, 115, 119, 0.1)' }}>
                    <Check size={16} className="text-[#0D7377]" />
                  </div>
                  <span className="text-[#4A5568]">Private group support and guidance</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-full mt-1" style={{ background: 'rgba(13, 115, 119, 0.1)' }}>
                    <Check size={16} className="text-[#0D7377]" />
                  </div>
                  <span className="text-[#4A5568]">No office visit required to get started</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1 rounded-full mt-1" style={{ background: 'rgba(13, 115, 119, 0.1)' }}>
                    <Check size={16} className="text-[#0D7377]" />
                  </div>
                  <span className="text-[#4A5568]">Complete program at your own pace</span>
                </li>
              </ul>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src={images.ownerProfessional}
                  alt={`${businessInfo.owner.name} - ${businessInfo.owner.title}`}
                  className="w-full max-w-md mx-auto"
                />
              </div>
              {/* Info card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-lg hidden md:block border border-[#0D7377]/20">
                <p className="font-serif text-lg text-[#2C2F36]">{businessInfo.owner.name}</p>
                <p className="text-sm text-[#0D7377]">{businessInfo.owner.credentials}</p>
                <p className="text-xs text-[#718096] mt-1">{businessInfo.owner.title}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0D7377 0%, #14A3A8 50%, #0D7377 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute top-10 left-10 opacity-20 animate-float">
          <CherryBlossom size={60} color="white" />
        </div>
        <div className="absolute bottom-10 right-10 opacity-15 animate-float-slow">
          <CherryBlossom size={80} color="white" />
        </div>
        
        <div className="container-custom text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-4 text-white">
            Ready to Start Your Transformation?
          </h2>
          <p className="text-xl text-white/90 mb-2">
            Cleanse. Restore. Renew.
          </p>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Your transformation starts with Pure Renewal. Contact us today to learn more 
            about the program and how to get started.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${businessInfo.phone.replace(/[^0-9]/g, '')}`}
              className="px-8 py-4 bg-white text-[#0D7377] rounded-full font-semibold hover:bg-white/90 transition-all hover:scale-105 inline-flex items-center justify-center gap-2 shadow-lg"
            >
              <Phone size={18} />
              {businessInfo.phone}
            </a>
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Request Information
              <ArrowRight size={18} />
            </Link>
          </div>
          
          <p className="mt-6 text-white/70 text-sm">
            Call or text for more information
          </p>
        </div>
      </section>
    </div>
  );
};

export default PureRenewalPage;
